import { useCallback, useEffect, useState } from "react";
import Avatar from "../components/basic/avatar";
import LoadingState from "../components/basic/loading-state";
import Queries from "../params/queries";
import Mutations from "../params/mutations";
import { formatRelative, igHandleProfileUrl } from "./shared";
import { contactAvatarMediaUrl, resolveMediaUrl } from "../helpers/media-url";
import type { AccountRow, FetchApi, FollowersResult } from "../params/types";

interface FollowersProps {
  accounts?: AccountRow[];
  /** Dashboard account list is still in flight. */
  loadingAccounts?: boolean;
  fetchApi?: FetchApi;
  setError?: (message: string) => void;
  flashSuccess?: (message: string) => void;
  liveRevision?: number;
}

export default function Followers({
  accounts = [],
  loadingAccounts = false,
  fetchApi,
  setError,
  flashSuccess,
  liveRevision = 0,
}: FollowersProps) {
  const [selected, setSelected] = useState<string | null>(
    () => accounts[0]?.username || null,
  );
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<FollowersResult | null>(null);
  const [busyUser, setBusyUser] = useState<string | null>(null);

  useEffect(() => {
    if (!accounts.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset when accounts empty
      setSelected(null);
      return;
    }
    if (!selected || !accounts.some((a) => a.username === selected)) {
      setSelected(accounts[0].username);
    }
  }, [accounts, selected]);

  const reload = useCallback(() => {
    if (!selected || !fetchApi) {
      setResult(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchApi(Queries.Followers, {
      account: selected,
      limit: 500,
    }).then((res) => {
      if (cancelled) return;
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to load followers");
        setResult(null);
      } else {
        setResult(res?.followers || null);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selected, fetchApi, setError]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reload() sets loading/result synchronously to kick off the fetch
    return reload();
  }, [reload, liveRevision]);

  const toggleIgnore = async (username: string, ignored: boolean) => {
    if (!selected || !fetchApi || busyUser) return;
    setBusyUser(username);
    try {
      const res = await fetchApi(Mutations.SetFollowerIgnored, {
        account: selected,
        username,
        ignored,
      });
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to update ignore");
        return;
      }
      const next = res?.set_follower_ignored as FollowersResult | undefined;
      if (next?.followers) {
        setResult(next);
      } else {
        const again = await fetchApi(Queries.Followers, {
          account: selected,
          limit: 500,
        });
        if (!again?.errors && again?.followers) {
          setResult(again.followers);
        }
      }
      flashSuccess?.(
        ignored ? `@${username} ignored` : `@${username} resumed`,
      );
    } finally {
      setBusyUser(null);
    }
  };

  if (!accounts.length) {
    return (
      <div className="ib-followers-view">
        {loadingAccounts ? (
          <LoadingState label="Loading accounts…" />
        ) : (
          <div className="ib-accounts-table__empty">
            <div className="ib-accounts-table__empty-title">No accounts yet</div>
            <div className="ib-accounts-table__empty-text">
              Add accounts to see their known followers here.
            </div>
          </div>
        )}
      </div>
    );
  }

  const active = accounts.find((a) => a.username === selected) || null;
  const followers = result?.followers || [];

  return (
    <div className="ib-followers-view">
      <div className="ib-flw-users">
        <div className="ib-flw-users__header">
          {accounts.length} account{accounts.length !== 1 ? "s" : ""}
        </div>
        <div className="ib-flw-users__items">
          {accounts.map((acc) => {
            const fullName =
              `${acc.first_name || ""} ${acc.last_name || ""}`.trim();
            return (
              <div
                key={acc.username}
                className={`ib-flw-user-item${
                  selected === acc.username ? " ib-flw-user-item--active" : ""
                }`}
                role="button"
                tabIndex={0}
                aria-label={`Select account ${acc.username}`}
                onClick={() => setSelected(acc.username)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelected(acc.username);
                  }
                }}
              >
                <Avatar
                  width={50}
                  height={50}
                  text={acc.ig_username || acc.username}
                  image={resolveMediaUrl(acc.avatar_url) || null}
                  colorful
                />
                <div className="ib-flw-user-item__body">
                  <span className="ib-flw-user-item__username">
                    {acc.username}
                  </span>
                  <span className="ib-flw-user-item__fullname">
                    {fullName ||
                      (acc.ig_username ? `@${acc.ig_username}` : "—")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="ib-flw-panel">
        {active && (
          <>
            <div className="ib-flw-panel__header">
              <Avatar
                width={50}
                height={50}
                text={active.ig_username || active.username}
                image={resolveMediaUrl(active.avatar_url) || null}
                colorful
              />
              <div className="ib-flw-panel__header-info">
                <span className="ib-flw-panel__username">
                  {active.username}
                </span>
                <span className="ib-flw-panel__count">
                  {loading
                    ? "Loading…"
                    : `${result?.total_known ?? 0} known${
                        result?.seeded === false ? " · seeding" : ""
                      }`}
                </span>
              </div>
            </div>

            <div className="ib-flw-panel__list">
              {loading && followers.length === 0 && (
                <LoadingState label="Loading followers…" />
              )}
              {!loading && followers.length === 0 && (
                <div className="ib-empty">
                  No known followers yet. Run the bot to seed the list.
                </div>
              )}
              {followers.map((f) => {
                const ignored = Boolean(f.ignored);
                const profileUrl =
                  igHandleProfileUrl(f.username) ||
                  `https://www.instagram.com/${encodeURIComponent(f.username)}/`;
                return (
                  <div
                    key={f.username}
                    className={`ib-follower-card${
                      ignored ? " ib-follower-card--ignored" : ""
                    }`}
                  >
                    <a
                      className="ib-follower-card__profile"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open @${f.username} on Instagram`}
                    >
                      <Avatar
                        width={50}
                        height={50}
                        text={f.username}
                        image={
                          contactAvatarMediaUrl(selected, f.username) || null
                        }
                        colorful
                      />
                      <div className="ib-follower-card__info">
                        <div className="ib-follower-card__top">
                          <span className="ib-follower-card__username">
                            @{f.username}
                          </span>
                          {ignored && (
                            <span className="ib-follower-card__badge">
                              ignored
                            </span>
                          )}
                        </div>
                        <span className="ib-follower-card__fullname">
                          {f.first_seen_at
                            ? `first seen ${formatRelative(f.first_seen_at)}`
                            : "first seen —"}
                        </span>
                      </div>
                    </a>
                    <button
                      type="button"
                      className="ib-follower-card__action"
                      disabled={busyUser === f.username}
                      onClick={() => toggleIgnore(f.username, !ignored)}
                    >
                      {busyUser === f.username
                        ? "…"
                        : ignored
                          ? "Resume"
                          : "Ignore"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
